import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Printer } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

const OTHER = "__other__";
const NEW = "__new__";

export const PosterDialog = ({ open, onOpenChange, isAdmin, onCreate }) => {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const { user } = useAuth();
  const [showPhone, setShowPhone] = useState(false);
  const [showWeight, setShowWeight] = useState(false);
  const [phone, setPhone] = useState("");
  useEffect(() => {
    if (!open) return;
    setShowWeight(false); localStorage.setItem("fautras_poster_empty_weight", "false");
    setPhone(user?.phone || "");
    setShowPhone(user?.role !== "admin" && !!(user?.phone || "").trim());
  }, [open, user]);
  const [events, setEvents] = useState([]);
  const [choice, setChoice] = useState("");
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    if (open) api.get("/events").then(({ data }) => {
      const list = data || [];
      setEvents(list.filter((e) => e.kind !== "other"));
      const last = list.find((e) => e.last);
      const own = list.find((e) => e.own);
      if (last?.kind === "other") { setChoice(OTHER); setName(last.name); }
      else if (last || own) { setChoice((last || own).id); setName((last || own).name); }
    }).catch(() => {});
  }, [open]);
  const pick = (v) => { setChoice(v); setNewName(""); setName(v === OTHER || v === NEW ? "" : (events.find((e) => e.id === v)?.name || "")); };
  const addEvent = async () => {
    setAdding(true);
    try {
      const { data } = await api.post("/events", { name: newName.trim() });
      setEvents((xs) => (xs.some((e) => e.id === data.id) ? xs : [...xs, data]));
      setChoice(data.id); setName(data.name); setNewName("");
      toast.success(t("Événement ajouté à la liste"));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setAdding(false); }
  };
  const [resellers, setResellers] = useState([]);
  const [targets, setTargets] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && isAdmin && !resellers.length) api.get("/admin/resellers").then(({ data }) => setResellers(data || [])).catch(() => {});
  }, [open, isAdmin, resellers.length]);

  const submit = async () => {
    setBusy(true);
    try {
      await onCreate(name.trim(), Object.keys(targets).filter((k) => targets[k]), showPhone ? phone.trim() : "");
      api.put("/me/last-place", { name: name.trim() }).catch(() => {});
      setName(""); setChoice(""); setTargets({});
    }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="poster-dialog">
        <DialogHeader>
          <DialogTitle>{t("Créer une affiche prix")}</DialogTitle>
          <DialogDescription>{t("La configuration (modèle, options, promo) est enregistrée dans « Véhicules à la vente » et l'affiche A4 s'affiche pour contrôle.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>{t("Événement ou lieu de stockage (titre de l'affiche)")}</Label>
            <Select value={choice} onValueChange={pick}>
              <SelectTrigger className="mt-1.5" data-testid="poster-event-select"><SelectValue placeholder={t("Choisir dans la liste")} /></SelectTrigger>
              <SelectContent>
                {[["event", "Événements"], ["stock", "Lieux de stockage"]].map(([k, lbl]) => events.some((e) => e.kind === k) && (
                  <SelectGroup key={k}>
                    <SelectLabel>{t(lbl)}</SelectLabel>
                    {events.filter((e) => e.kind === k).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectGroup>
                ))}
                <SelectItem value={NEW} data-testid="poster-event-new">{t("+ Créer un événement…")}</SelectItem>
                <SelectItem value={OTHER} data-testid="poster-event-other">{t("Autre (une seule fois)…")}</SelectItem>
              </SelectContent>
            </Select>
            {choice === OTHER && <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("Ex. Salon du cheval d'Angers 2026")} className="mt-2" data-testid="poster-salon-name" />}
            {choice === NEW && (
              <div className="flex gap-2 mt-2">
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t("Ex. Equita Lyon 2026")} data-testid="poster-new-event-name" onKeyDown={(e) => { if (e.key === "Enter" && newName.trim()) addEvent(); }} />
                <Button type="button" onClick={addEvent} disabled={!newName.trim() || adding} data-testid="poster-new-event-add">{adding ? <Loader2 className="w-4 h-4 animate-spin" /> : t("Ajouter")}</Button>
              </div>
            )}
            {choice === NEW && <p className="text-[11px] text-muted-foreground mt-1">{t("L'événement sera ajouté à la liste pour tous les vendeurs.")}</p>}
          </div>
          <div data-testid="poster-phone-block">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={showPhone} onCheckedChange={(v) => setShowPhone(!!v)} data-testid="poster-show-phone" /> {t("Afficher mon numéro de téléphone sur l'affiche")}
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer mt-2">
              <Checkbox checked={showWeight} onCheckedChange={(v) => { setShowWeight(!!v); localStorage.setItem("fautras_poster_empty_weight", v ? "true" : "false"); }} data-testid="poster-show-empty-weight" /> {t("Afficher le poids à vide")}
            </label>
            {showPhone && <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Ex. 06 12 34 56 78" className="mt-2" data-testid="poster-phone" />}
          </div>
          {isAdmin && resellers.length > 0 && (
            <div>
              <Label className="mb-2 block">{t("Revendeurs ayant accès (aucun coché = tous)")}</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-48 overflow-auto pr-1">
                {resellers.map((r) => (
                  <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer rounded-md border border-border px-2 py-1.5" data-testid={`poster-reseller-${r.id}`}>
                    <Checkbox checked={!!targets[r.id]} onCheckedChange={(v) => setTargets((s) => ({ ...s, [r.id]: !!v }))} />
                    <span className="truncate">{r.name}{r.company ? ` · ${r.company}` : ""}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="poster-cancel">{t("Annuler")}</Button>
          <Button onClick={submit} disabled={!name.trim() || busy || (showPhone && !phone.trim())} className="gap-2" data-testid="poster-create">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} {t("Créer l'affiche")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
